import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Star, MessageSquareHeart, Mic, FileText } from "lucide-react";
import api from "@/lib/apiClient";
import { EmptyState, StatCard } from "@/components/dashboard/primitives";

function Stars({ value }) {
  return <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((s) => <Star key={s} className={`h-3.5 w-3.5 ${s <= value ? "fill-accent text-accent" : "text-border"}`} />)}</div>;
}

export default function EventFeedback() {
  const { event } = useOutletContext();
  const [data, setData] = useState({ items: [], average: 0, speaker_average: 0, content_average: 0, count: 0, distribution: {} });

  useEffect(() => {
    api.get(`/events/${event.id}/feedback`).then(({ data }) => setData(data)).catch(() => {});
  }, [event.id]);

  const max = Math.max(1, ...Object.values(data.distribution || {}));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Star} label="Overall" value={data.average || "—"} accent />
        <StatCard icon={Mic} label="Speaker" value={data.speaker_average || "—"} />
        <StatCard icon={FileText} label="Content" value={data.content_average || "—"} />
        <StatCard icon={MessageSquareHeart} label="Responses" value={data.count} />
      </div>

      {data.count > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-display font-bold text-foreground mb-4">Overall rating distribution</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((r) => (
              <div key={r} className="flex items-center gap-3 text-sm">
                <span className="w-4 text-muted-foreground">{r}</span>
                <Star className="h-3.5 w-3.5 fill-accent text-accent" />
                <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-accent rounded-full" style={{ width: `${((data.distribution[r] || 0) / max) * 100}%` }} /></div>
                <span className="w-8 text-right text-muted-foreground">{data.distribution[r] || 0}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.items.length === 0 ? (
        <EmptyState icon={MessageSquareHeart} title="No feedback yet" description="Session ratings and comments from attendees will show here." />
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="feedback-list">
          {data.items.map((f) => (
            <div key={f.id} className="p-4">
              <div className="flex items-center gap-4 flex-wrap">
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">Overall <Stars value={f.overall_rating} /></span>
                {f.speaker_rating && <span className="flex items-center gap-1.5 text-xs text-muted-foreground">Speaker <Stars value={f.speaker_rating} /></span>}
                {f.content_rating && <span className="flex items-center gap-1.5 text-xs text-muted-foreground">Content <Stars value={f.content_rating} /></span>}
              </div>
              {f.comment && <p className="mt-2 text-sm text-foreground">{f.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
